import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Firestore,
} from 'firebase/firestore';
import type {
  CreateEventInput,
  Event,
  EventRepository,
  Unsubscribe,
  UpdateEventInput,
} from '@nx/domain';
import { mapEventDoc, snapshotData } from './firestoreMappers';

const EVENTS = 'events';

export class FirebaseEventRepository implements EventRepository {
  constructor(private readonly db: Firestore) {}

  async listByOrganizer(organizerId: string): Promise<Event[]> {
    const q = query(collection(this.db, EVENTS), where('organizerId', '==', organizerId));
    return new Promise((resolve, reject) => {
      const unsub = onSnapshot(
        q,
        (snap) => {
          unsub();
          resolve(snap.docs.map((d) => mapEventDoc(d.id, snapshotData(d))));
        },
        reject,
      );
    });
  }

  watchByOrganizer(
    organizerId: string,
    listener: (events: Event[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(collection(this.db, EVENTS), where('organizerId', '==', organizerId));
    return onSnapshot(
      q,
      (snap) => {
        listener(snap.docs.map((d) => mapEventDoc(d.id, snapshotData(d))));
      },
      (error) => {
        onError?.(error instanceof Error ? error : new Error(String(error)));
      },
    );
  }

  async getById(eventId: string): Promise<Event | null> {
    const snap = await getDoc(doc(this.db, EVENTS, eventId));
    if (!snap.exists()) return null;
    return mapEventDoc(snap.id, snapshotData(snap));
  }

  async create(organizerId: string, input: CreateEventInput): Promise<Event> {
    const ref = await addDoc(collection(this.db, EVENTS), {
      title: input.title.trim(),
      description: input.description?.trim() ?? null,
      location: input.location.trim(),
      coverImageUrl: input.coverImageUrl ?? null,
      startDate: input.startDate,
      endDate: input.endDate,
      maxParticipants: input.maxParticipants ?? null,
      registrationDeadline: input.registrationDeadline ?? null,
      registrationFormFields: input.registrationFormFields ?? [],
      publicAgendaToken: null,
      status: 'draft',
      organizerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const created = await this.getById(ref.id);
    if (!created) throw new Error('Failed to load created event');
    return created;
  }

  async update(eventId: string, input: UpdateEventInput): Promise<Event> {
    const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (input.title !== undefined) patch.title = input.title.trim();
    if (input.description !== undefined) patch.description = input.description?.trim() ?? null;
    if (input.location !== undefined) patch.location = input.location.trim();
    if (input.coverImageUrl !== undefined) patch.coverImageUrl = input.coverImageUrl;
    if (input.startDate !== undefined) patch.startDate = input.startDate;
    if (input.endDate !== undefined) patch.endDate = input.endDate;
    if (input.maxParticipants !== undefined) patch.maxParticipants = input.maxParticipants;
    if (input.registrationDeadline !== undefined) patch.registrationDeadline = input.registrationDeadline;
    if (input.registrationFormFields !== undefined) {
      patch.registrationFormFields = input.registrationFormFields;
    }
    if (input.status !== undefined) patch.status = input.status;
    if (input.publicAgendaToken !== undefined) patch.publicAgendaToken = input.publicAgendaToken;
    await updateDoc(doc(this.db, EVENTS, eventId), patch);
    const updated = await this.getById(eventId);
    if (!updated) throw new Error('Event not found after update');
    return updated;
  }

  async delete(eventId: string): Promise<void> {
    await deleteDoc(doc(this.db, EVENTS, eventId));
  }
}
